import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember,
  GetGuestsByEventIdAndMobile, AddGuestToFunctions, AddOrMoveFamilyMember, UpdateGuest,
  SetGuestFunctions,
} from '../../api/api';
import { isMarriageEventType, normaliseText } from '../../utils/helpers';
import { contactsSupported, pickContactFromDevice, bulkContactsSupported, getDeviceContacts } from '../../utils/contacts';
import { GUEST_SIDE_STORAGE_KEY } from '../../components/Layout';
import { shareInvite } from '../../utils/whatsappInvite';

export const MAX_FAMILY_SIZE = 9; // 1 family head + up to 8 other members
export const INVITE_COOLDOWN_SECONDS = 10;
const ten = (v) => v.replace(/\D/g, '').slice(0, 10);
const emptyToNull = (obj) => {
  const out = {};
  for (const k of Object.keys(obj)) out[k] = obj[k] === '' ? null : obj[k];
  return out;
};
const blankForm = () => ({
  FunctionId: '', FunctionIds: [], Surname: '', GuestName: '', FatherHusband: '', MobileNumber: '',
  Side: 'Common', Relation: '', GroupName: '', EmailId: '', ExpectedCount: 1,
  Visibility: 'Private', InviteStatus: 'Pending', InvitedVia: '', Remarks: '',
});

const guestGroupKey = (row) => {
  const owner = row.CreatedBy || 'admin';
  const mobile = String(row.MobileNumber || '').trim();
  if (mobile) return `${owner}:mobile:${mobile}`;
  return `${owner}:name:${[row.Surname, row.GuestName, row.FatherHusband].map(v => String(v || '').trim().toLowerCase()).join('|')}`;
};

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

const combineGuestRows = (list) => {
  const groups = new Map();
  list.forEach(row => {
    const key = guestGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        ...row,
        _rows: [],
        _guestIds: [],
        _functionIds: [],
        FunctionNames: '',
      });
    }
    const group = groups.get(key);
    group._rows.push(row);
    group._guestIds.push(row.GuestId);
    rowFunctionIds(row).forEach(id => {
      if (!group._functionIds.some(existing => String(existing) === String(id))) group._functionIds.push(id);
    });
    const names = String(row.FunctionNames || row.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean);
    names.forEach(name => {
      if (!group.FunctionNames.split(', ').includes(name)) {
        group.FunctionNames = group.FunctionNames ? `${group.FunctionNames}, ${name}` : name;
      }
    });
    group.ExpectedCount = Math.max(Number(group.ExpectedCount || 1), Number(row.ExpectedCount || 1));
  });
  return Array.from(groups.values());
};

// Sortable columns of the guest list: key → value extractor. Order of
// SORT_OPTIONS is what the "Sort" dropdown shows.
const lc = (v) => (v || '').toString().trim().toLowerCase();
export const SORT_KEYS = {
  name: (r) => lc([r.Surname, r.GuestName, r.FatherHusband].filter(Boolean).join(' ')),
  firstName: (r) => lc(r.GuestName),
  mobile: (r) => lc(r.MobileNumber),
  function: (r) => lc(r.FunctionNames || r.FunctionName),
  side: (r) => lc(r.Side),
  pax: (r) => Number(r.ExpectedCount || 1),
  visibility: (r) => lc(r.Visibility || 'Private'),
  addedBy: (r) => lc(r.AddedByName || r.InvitedByName),
  status: (r) => lc(r.InviteStatus),
  rsvp: (r) => lc(r.RSVPStatus),
  city: (r) => lc(r.City),
  added: (r) => lc(r.CreatedDate),
};
export const SORT_OPTIONS = [
  ['name', 'Surname + Name'], ['firstName', 'First name'], ['mobile', 'Mobile'], ['function', 'Function'],
  ['side', 'Side'], ['pax', 'Persons'], ['visibility', 'Visibility'], ['addedBy', 'Added by'],
  ['status', 'Status'], ['rsvp', 'RSVP'], ['city', 'City'], ['added', 'Date added'],
];

// All Members logic — shared by the desktop and mobile UIs.
export function useMembers(event) {
  const { user, organiser, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = can('CanMembers');
  const canAdd = can('CanMembersAdd') || can('CanMembers');
  const canEdit = can('CanMembersEdit') || can('CanMembers');
  const canDelete = can('CanMembersDelete') || can('CanMembers');
  const mine = (row) => isAdmin || String(row.CreatedBy) === String(me);

  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // `?fn=<FunctionId>` (from the Functions / Headcount drill-in) preselects the filter.
  const [searchParams] = useSearchParams();
  const [fnFilter, setFnFilter] = useState(() => searchParams.get('fn') || '');
  const [sideFilter, setSideFilter] = useState('');
  const [guestSide, setGuestSide] = useState(() => localStorage.getItem(GUEST_SIDE_STORAGE_KEY) || 'Bride');
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const sideOptions = isMarriage ? ['Common', 'Groom', 'Bride'] : ['Common'];
  const defaultSide = isMarriage ? guestSide : 'Common';

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => ({ ...blankForm(), Side: defaultSide }));
  const [party, setParty] = useState([]);
  const [familyMode, setFamilyMode] = useState(false);
  const [addMode, setAddMode] = useState('new');
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [checkedMobile, setCheckedMobile] = useState('');
  const [inviteRow, setInviteRow] = useState(null);
  const [inviteSharing, setInviteSharing] = useState('');
  const [inviteCooldown, setInviteCooldown] = useState(0);

  // ── Bulk "import from contacts" state ──────────────────────────────────────
  const canBulkImport = bulkContactsSupported();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [deviceContacts, setDeviceContacts] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkFunctionIds, setBulkFunctionIds] = useState([]);
  const [bulkSide, setBulkSide] = useState(() => defaultSide);
  const [bulkVisibility, setBulkVisibility] = useState('Private');

  useEffect(() => {
    const onSideChange = (event) => setGuestSide(event.detail || localStorage.getItem(GUEST_SIDE_STORAGE_KEY) || 'Bride');
    window.addEventListener('guest-side-change', onSideChange);
    return () => window.removeEventListener('guest-side-change', onSideChange);
  }, []);

  useEffect(() => {
    if (inviteCooldown <= 0) return undefined;
    const timer = setTimeout(() => setInviteCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [inviteCooldown]);

  const fnName = useCallback(
    (id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '—',
    [functions]
  );

  const loadFunctions = useCallback(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => {});
  }, [event]);

  const refresh = useCallback(() => {
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load members'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);
  useEffect(() => { refresh(); }, [refresh]);

  const distinctPeople = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.MobileNumber || `name:${r.GuestName}`;
      if (!map.has(key)) map.set(key, r);
    });
    return Array.from(map.values());
  }, [rows]);

  const addersByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (!r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      m.get(r.MobileNumber).add(String(r.CreatedBy));
    });
    return m;
  }, [rows]);
  const adderCount = (row) => (row.MobileNumber ? (addersByMobile.get(row.MobileNumber)?.size || 1) : 1);

  // Sort: { key, dir }. Keys map to a comparable value per row (see SORT_KEYS).
  // Clicking the same column header again flips the direction.
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const filtered = useMemo(() => {
    let list = rows;
    if (fnFilter) list = list.filter(r => rowFunctionIds(r).includes(String(fnFilter)));
    if (sideFilter) list = list.filter(r => String(r.Side || '') === String(sideFilter));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(r => JSON.stringify(r).toLowerCase().includes(s));
    }
    const get = SORT_KEYS[sort.key] || SORT_KEYS.name;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return combineGuestRows(list).sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }, [rows, fnFilter, sideFilter, q, sort]);

  const stats = useMemo(() => {
    const persons = filtered.reduce((sum, r) => sum + Number(r.ExpectedCount || 1), 0);
    const entries = filtered.reduce((sum, r) => sum + (r._rows?.length || 1), 0);
    const bride = filtered.filter(r => r.Side === 'Bride' || r.Side === 'Female').length;
    const groom = filtered.filter(r => r.Side === 'Groom' || r.Side === 'Male').length;
    return { entries, parties: filtered.length, persons, bride, groom };
  }, [filtered]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedFunctionIds = Array.isArray(form.FunctionIds) ? form.FunctionIds.map(String) : [];
  const toggleFunction = (functionId) => {
    const id = String(functionId);
    setForm(f => {
      const current = Array.isArray(f.FunctionIds) ? f.FunctionIds.map(String) : [];
      const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
      return { ...f, FunctionIds: next, FunctionId: next[0] || '' };
    });
  };
  const addParty = () => setParty(p => (p.length >= MAX_FAMILY_SIZE - 1 ? p : [...p, { Surname: '', GuestName: '', MobileNumber: '' }]));
  const setPartyField = (i, k, v) => setParty(p => p.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const removeParty = (i) => setParty(p => p.filter((_, idx) => idx !== i));

  const openAdd = (mode = 'individual') => {
    const initialFunctions = fnFilter ? [String(fnFilter)] : [];
    setForm({ ...blankForm(), Side: defaultSide, FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions });
    setFamilyMode(mode === 'family');
    setParty(mode === 'family' ? [{ Surname: '', GuestName: '', MobileNumber: '' }] : []);
    setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(null); setOpen(true);
  };
  // Back-key support: reset the form to blank but keep it open (clear-then-close).
  const clearForm = () => {
    const initialFunctions = fnFilter ? [String(fnFilter)] : [];
    setForm({ ...blankForm(), Side: defaultSide, FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions });
    setParty(familyMode ? [{ Surname: '', GuestName: '', MobileNumber: '' }] : []);
    setAddMode('new'); setDupes([]); setCheckedMobile('');
  };
  // Dirty = the user typed anything meaningful (lead fields or any party rows).
  const isDirty = () => {
    const b = blankForm();
    const changed = ['GuestName', 'Surname', 'FatherHusband', 'MobileNumber', 'Relation', 'GroupName', 'Remarks']
      .some(k => String(form[k] ?? '') !== String(b[k] ?? ''));
    return changed || party.length > 0;
  };
  const openEdit = (row) => {
    const functionIds = rowFunctionIds(row);
    setForm({
      FunctionId: functionIds[0] || '', FunctionIds: functionIds,
      Surname: row.Surname ?? '', GuestName: row.GuestName ?? '',
      FatherHusband: row.FatherHusband ?? '', MobileNumber: row.MobileNumber ?? '',
      Side: row.Side ?? 'Common', Relation: row.Relation ?? '', GroupName: row.GroupName ?? '',
      EmailId: row.EmailId ?? '', ExpectedCount: 1,
      Visibility: row.Visibility ?? 'Private',
      InviteStatus: row.InviteStatus ?? 'Pending', InvitedVia: row.InvitedVia ?? '', Remarks: row.Remarks ?? '',
    });
    setFamilyMode(false);
    setParty([]); setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(row); setOpen(true);
  };

  const canImport = contactsSupported();
  const importContact = async () => {
    try {
      const c = await pickContactFromDevice();
      if (!c) return;
      setForm(f => ({
        ...f,
        Surname: normaliseText(c.surname) || f.Surname,
        GuestName: normaliseText(c.name) || f.GuestName,
        FatherHusband: normaliseText(c.fatherHusband) || f.FatherHusband,
        MobileNumber: c.mobile || f.MobileNumber,
      }));
      if (c.mobile) await checkMobile(c.mobile);
    } catch (e) { toast(e.message || 'Could not read contacts'); }
  };

  const pickExisting = (guestId) => {
    const r = rows.find(x => String(x.GuestId) === String(guestId));
    if (!r) return;
    setForm(f => ({
      ...f,
      Surname: r.Surname ?? '', GuestName: r.GuestName ?? '', FatherHusband: r.FatherHusband ?? '',
      MobileNumber: r.MobileNumber ?? '', Side: r.Side ?? 'Common',
      Relation: r.Relation ?? '', GroupName: r.GroupName ?? '',
      EmailId: r.EmailId ?? '', ExpectedCount: r.ExpectedCount ?? 1,
    }));
    checkMobile(r.MobileNumber);
  };

  const checkMobile = async (mobile) => {
    const m = String(mobile || '').trim();
    if (!m) { setDupes([]); setCheckedMobile(''); return []; }
    let res = [];
    try {
      const r = await GetGuestsByEventIdAndMobile({ EventId: event.EventId, MobileNumber: m });
      res = Array.isArray(r) ? r : [];
    } catch { res = []; }
    setDupes(res); setCheckedMobile(m);
    return res;
  };

  const blockedSameFn = !editing && dupes.some(
    d => String(d.CreatedBy) === String(me) && rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id)))
  );
  const hasOtherInvites = !editing && dupes.length > 0 && !blockedSameFn;
  const needsCheck = !editing && checkedMobile !== String(form.MobileNumber).trim();

  const save = async () => {
    if (!selectedFunctionIds.length) return toast('Pick at least one function');
    if (!String(form.GuestName).trim()) return toast('Name is required');
    if (!/^\d{10}$/.test(String(form.MobileNumber).trim())) return toast('Enter a valid 10-digit mobile number');
    if (familyMode) {
      if (!party.length || party.some(m => !String(m.GuestName || '').trim())) {
        return toast('Name is required for every family member');
      }
      if (party.some(m => !/^\d{10}$/.test(String(m.MobileNumber || '').trim()))) {
        return toast('Enter a valid 10-digit phone number for every family member');
      }
    }

    setSaving(true);
    try {
      // One-click save: dedup-check inline. Block only a true duplicate (the same
      // mobile already in MY list for THIS function); otherwise save right away.
      let dupeRows = dupes;
      if (!editing) {
        dupeRows = await checkMobile(form.MobileNumber);
        const blocked = dupeRows.some(d => String(d.CreatedBy) === String(me) && rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id))));
        if (blocked) { setSaving(false); return toast('You already added this guest to one of the selected functions'); }
      }
      const dupExists = !editing && dupeRows.length > 0;
      const { FunctionIds, ...singleForm } = form;
      const payload = emptyToNull({ ...singleForm, Side: isMarriage ? singleForm.Side : 'Common', ExpectedCount: 1 });
      if (editing) {
        await UpdateGuest({ data: { ...payload, UpdatedBy: me }, where: `GuestId = ${editing.GuestId}` });
        await SetGuestFunctions({
          GuestId: editing.GuestId,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          FunctionIds: selectedFunctionIds,
          UpdatedBy: me,
        });
        toast('Updated');
      } else {
        const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || '';
        const familyMembers = familyMode
          ? party.filter(m => String(m.GuestName || '').trim() || String(m.Surname || '').trim())
          .map(m => ({
            Surname: m.Surname || null,
            GuestName: m.GuestName || null,
            MobileNumber: m.MobileNumber ? ten(m.MobileNumber) : null,
          }))
          : [];
        const res = await AddGuestToFunctions({
          ...payload,
          FunctionIds: selectedFunctionIds,
          FamilyMembers: familyMembers,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          AddedByName: addedByName || null,
          AddedByMobile: user.MobileNumber || null,
          Valid: 1,
          CreatedBy: me,
          UpdatedBy: me,
        });
        const familyRows = res?.familyRows || 1;
        toast(familyMode
          ? `Family of ${familyRows} added`
          : (dupExists ? 'Guest added to selected functions' : 'Guest added'));
      }
      setOpen(false); refresh();
    } catch {
      toast('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const mark = async (row, status) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { InviteStatus: status, UpdatedBy: me }, where: `GuestId = ${id}` })));
      setRows(rs => rs.map(r => ids.includes(r.GuestId) ? { ...r, InviteStatus: status } : r));
    } catch { toast('Could not update status'); }
  };

  const del = async (row) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    const suffix = ids.length > 1 ? ` from ${ids.length} functions` : '';
    if (!window.confirm(`Remove ${row.GuestName || 'this member'}${suffix} from the list?`)) return;
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId = ${id}` })));
      toast('Removed'); refresh();
    } catch { toast('Remove failed'); }
  };

  const guestHasAllFunctions = (row) => {
    if (!functions.length) return false;
    const ids = rowFunctionIds(row).map(String);
    return functions.every(f => ids.includes(String(f.FunctionId)));
  };

  const openInvite = (row) => {
    setInviteRow(row);
  };
  const closeInvite = () => {
    if (inviteSharing) return;
    setInviteRow(null);
  };
  const sendInvite = async (kind) => {
    if (!inviteRow) return;
    if (kind === 'pdf' && !guestHasAllFunctions(inviteRow)) {
      toast('PDF invite is only for guests invited to all functions');
      return;
    }
    setInviteSharing(kind);
    try {
      await shareInvite(kind, inviteRow, event, organiser);
      await mark(inviteRow, 'Invited');
      setInviteCooldown(INVITE_COOLDOWN_SECONDS);
      toast('Invite sent');
    } catch (err) {
      toast(err?.message || 'WhatsApp invite failed');
    } finally {
      setInviteSharing('');
    }
  };

  const addOrMoveFamilyMember = async (head, member) => {
    const headName = [head.Surname, head.GuestName].filter(Boolean).join(' ').trim() || 'the family head';
    try {
      const result = await AddOrMoveFamilyMember({
        HeadGuestId: head.GuestId,
        ExistingGuestId: member.ExistingGuestId || null,
        EventId: event.EventId,
        OrganiserId: organiser.OrganiserId,
        FunctionIds: rowFunctionIds(head),
        Surname: member.Surname || null,
        GuestName: member.GuestName || null,
        MobileNumber: member.MobileNumber || null,
        Side: member.Side || head.Side,
        Visibility: member.Visibility || head.Visibility,
        Remarks: member.Remarks || null,
        AddedByName: `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null,
        AddedByMobile: user.MobileNumber || null,
        CreatedBy: me,
        UpdatedBy: me,
      });
      const memberName = [result?.Surname, result?.GuestName].filter(Boolean).join(' ').trim() || 'Guest';
      if (result?.action === 'moved') toast(`${memberName} was moved to ${headName}'s Family`);
      else if (result?.action === 'already-member') toast(`${memberName} is already in ${headName}'s Family`);
      else toast(`${memberName} was added to ${headName}'s Family`);
      refresh();
      return result;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Could not add family member';
      toast(message);
      throw err;
    }
  };

  // setMobile keeps the dedup state honest when the number changes.
  const setMobile = (v) => { setF('MobileNumber', ten(v)); if (checkedMobile) { setDupes([]); setCheckedMobile(''); } };

  // ── Bulk import from the phone book ─────────────────────────────────────────
  const openBulk = async () => {
    setBulkSelected(new Set()); setBulkSearch(''); setBulkFunctionIds(fnFilter ? [String(fnFilter)] : []);
    setBulkSide(guestSide); setBulkVisibility('Private'); setBulkOpen(true);
    setBulkLoading(true);
    try {
      const list = await getDeviceContacts();
      setDeviceContacts(list);
    } catch (e) {
      toast(e.message || 'Could not read contacts'); setBulkOpen(false);
    } finally { setBulkLoading(false); }
  };
  const toggleBulk = (key) => setBulkSelected(s => {
    const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });
  // mobile → Set(FunctionId) already in MY list, so the picker can show "Added"
  // and only add the missing functions for a contact.
  const myFnsByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (String(r.CreatedBy) !== String(me) || !r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      rowFunctionIds(r).forEach(id => m.get(r.MobileNumber).add(String(id)));
    });
    return m;
  }, [rows, me]);
  // Functions (of the chosen ones) this contact still needs; [] = fully added already.
  const missingFnsFor = (c) => {
    const have = c.mobile ? myFnsByMobile.get(c.mobile) : null;
    return bulkFunctionIds.filter(id => !(have && have.has(String(id))));
  };
  const bulkVisible = useMemo(() => {
    const s = bulkSearch.trim().toLowerCase();
    const list = s ? deviceContacts.filter(c => `${c.display} ${c.mobile}`.toLowerCase().includes(s)) : deviceContacts;
    return list.map(c => ({ ...c, added: bulkFunctionIds.length > 0 && missingFnsFor(c).length === 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceContacts, bulkSearch, bulkFunctionIds, myFnsByMobile]);
  const bulkSelectable = bulkVisible.filter(c => !c.added);
  const bulkAllVisibleSelected = bulkSelectable.length > 0 && bulkSelectable.every(c => bulkSelected.has(c.key));
  const toggleBulkAll = () => setBulkSelected(s => {
    const n = new Set(s);
    if (bulkAllVisibleSelected) bulkSelectable.forEach(c => n.delete(c.key));
    else bulkSelectable.forEach(c => n.add(c.key));
    return n;
  });

  const saveBulk = async () => {
    if (!bulkFunctionIds.length) return toast('Pick at least one function');
    const chosen = deviceContacts.filter(c => bulkSelected.has(c.key));
    if (!chosen.length) return toast('Select at least one contact');
    setBulkSaving(true);
    setBulkProgress({ done: 0, total: chosen.length });
    let people = 0, invites = 0, skipped = 0;
    try {
      const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || '';
      // ONE API call per contact with only the functions they are not yet in
      // (the API inserts one Evt_Guests row per function).
      for (const c of chosen) {
        const fnIds = missingFnsFor(c);
        if (!fnIds.length) { skipped++; setBulkProgress(p => ({ ...p, done: p.done + 1 })); continue; }
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: fnIds,
          Surname: c.surname || null, GuestName: c.name || c.display || null, FatherHusband: c.fatherHusband || null,
          MobileNumber: c.mobile || null, Side: isMarriage ? bulkSide : 'Common', Visibility: bulkVisibility, ExpectedCount: 1,
          InviteStatus: 'Pending', AddedByName: addedByName || null, AddedByMobile: user.MobileNumber || null,
          Valid: 1, CreatedBy: me, UpdatedBy: me,
        });
        people++; invites += fnIds.length;
        setBulkProgress(p => ({ ...p, done: p.done + 1 }));
      }
      toast(`Added ${people} guest${people === 1 ? '' : 's'} (${invites} invite${invites === 1 ? '' : 's'})${skipped ? ` · ${skipped} already in your list` : ''}`);
      setBulkOpen(false); refresh();
    } catch {
      toast(`Added ${people}, then a save failed — check your connection and try again`);
      refresh();
    } finally { setBulkSaving(false); }
  };

  return {
    isAdmin, canView, canAdd, canEdit, canDelete, mine, isMarriage, sideOptions,
    functions, rows, loading, q, setQ, fnFilter, setFnFilter, sideFilter, setSideFilter, fnName,
    filtered, stats, adderCount, distinctPeople, sort, setSort, toggleSort, SORT_OPTIONS,
    open, setOpen, editing, form, setF, setMobile, selectedFunctionIds, toggleFunction,
    party, addParty, setPartyField, removeParty, familyMode,
    addMode, setAddMode, dupes, checkedMobile, blockedSameFn, hasOtherInvites, needsCheck,
    openAdd, openEdit, clearForm, isDirty, canImport, importContact, pickExisting, save, saving, mark, del, me,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    addOrMoveFamilyMember,
    // bulk import
    canBulkImport, bulkOpen, setBulkOpen, bulkLoading, bulkSaving, bulkProgress, deviceContacts, bulkVisible, bulkSelectable,
    bulkSelected, toggleBulk, bulkSearch, setBulkSearch, bulkAllVisibleSelected, toggleBulkAll,
    bulkFunctionIds, setBulkFunctionIds, bulkSide, setBulkSide, bulkVisibility, setBulkVisibility,
    openBulk, saveBulk,
  };
}
